import React, { useState } from 'react';
import { Layout as AntLayout, Menu, Button, Typography, Space, Dropdown } from 'antd';
import { 
  UserOutlined, 
  DashboardOutlined, 
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CrownOutlined,
  BarChartOutlined,
  MessageOutlined,
  FormOutlined,
  DollarOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  InstagramOutlined,
  TrophyOutlined,
  ScheduleOutlined,
  CompassOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';

const { Header, Sider, Content } = AntLayout;
const { Title } = Typography;

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
}

const AdminLayout: React.FC<LayoutProps> = ({ children, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: 'Users',
      onClick: () => navigate('/users'),
    },
    {
      key: '/subscriptions',
      icon: <CrownOutlined />,
      label: 'Subscriptions',
      onClick: () => navigate('/subscriptions'),
    },
    {
      key: '/wallets',
      icon: <DollarOutlined />,
      label: 'Wallets',
      onClick: () => navigate('/wallets'),
    },
    {
      key: '/invoices',
      icon: <FileTextOutlined />,
      label: 'Invoices',
      onClick: () => navigate('/invoices'),
    },
    {
      key: '/payments',
      icon: <CreditCardOutlined />,
      label: 'Payments',
      onClick: () => navigate('/payments'),
    },
    {
      key: '/feedback',
      icon: <MessageOutlined />,
      label: 'Try-On Feedback',
      onClick: () => navigate('/feedback'),
    },
    {
      key: '/surveys',
      icon: <FormOutlined />,
      label: 'User Surveys',
      onClick: () => navigate('/surveys'),
    },
    {
      key: '/instagram-batch',
      icon: <InstagramOutlined />,
      label: 'Instagram Batch',
      onClick: () => navigate('/instagram-batch'),
    },
    {
      key: '/quests',
      icon: <TrophyOutlined />,
      label: 'Quests',
      onClick: () => navigate('/quests'),
    },
    {
      key: '/tours',
      icon: <CompassOutlined />,
      label: 'Tours',
      onClick: () => navigate('/tours'),
    },
    {
      key: '/jobs',
      icon: <ScheduleOutlined />,
      label: 'Background Jobs',
      onClick: () => navigate('/jobs'),
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: 'Reports',
      onClick: () => navigate('/reports'),
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
  ];

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: onLogout,
    },
  ];

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/users')) return ['/users'];
    if (path.startsWith('/instagram-batch')) return ['/instagram-batch'];
    if (path.startsWith('/quests')) return ['/quests'];
    if (path.startsWith('/tours')) return ['/tours'];
    if (path.startsWith('/jobs')) return ['/jobs'];
    if (path.startsWith('/subscriptions')) return ['/subscriptions'];
    if (path.startsWith('/wallets')) return ['/wallets'];
    if (path.startsWith('/invoices')) return ['/invoices'];
    if (path.startsWith('/payments')) return ['/payments'];
    if (path.startsWith('/feedback')) return ['/feedback'];
    if (path.startsWith('/reports')) return ['/reports'];
    if (path.startsWith('/settings')) return ['/settings'];
    return ['/dashboard'];
  };

  return (
    <AntLayout style={{ minHeight: '100vh', height: '100vh', overflow: 'hidden' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        theme="light"
        style={{
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ 
          padding: collapsed ? '16px 8px' : '16px', 
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          {collapsed ? (
            <div style={{ 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              backgroundColor: '#1890ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>
              C
            </div>
          ) : (
            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
              Closet Admin
            </Title>
          )}
        </div>
        
        <Menu
          mode="inline"
          selectedKeys={getSelectedKeys()}
          items={menuItems}
          style={{ border: 'none' }}
        />
      </Sider>
      
      <AntLayout>
        <Header 
          style={{ 
            padding: '0 24px', 
            background: '#fff', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Space>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
          </Space>
          
          <Space>
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
            >
              <Button 
                type="text" 
                icon={<UserOutlined />}
                style={{ height: 'auto' }}
              >
                Admin
              </Button>
            </Dropdown>
          </Space>
        </Header>
        
        <Content
          style={{
            margin: 0,
            padding: 0,
            minHeight: 280,
            background: '#f5f5f5',
            overflow: 'auto',
            height: 'calc(100vh - 64px)', // Account for header
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default AdminLayout;